/**
 * useBoardHealthProbe
 *
 * Attaches to the board container ref and computes board health using:
 *   1. MutationObserver on the container (detects row add/remove, attribute changes)
 *   2. Periodic poll every 5 s (catches CSS-only changes MutationObserver misses)
 *
 * Health rules:
 *   - Container exists and has bounding-rect w > 0, h > 0
 *   - Computed style: display !== 'none', visibility !== 'hidden', opacity > 0.01
 *   - data-row count > 0  OR  an explicit ".board-empty-state" element is visible
 *   - Detects "latched fallback" via DOM (.board-fallback present while data > 0)
 *
 * Writes results to boardHealthStore for heartbeat consumption.
 * Returns React state (triggers re-renders) so self-healing sees fresh values.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { updateProbeResults, BoardBlankReason } from '../services/boardHealthStore.js';

const PROBE_INTERVAL_MS = 5000;

const INITIAL_STATE = {
  boardRenderOk: true,
  boardVisibleRowCount: 0,
  boardBlankReason: BoardBlankReason.HEALTHY,
  boardContainerRect: { w: 0, h: 0 },
};

/**
 * @param {Object}  opts
 * @param {React.RefObject} opts.containerRef  - ref to the board container DOM node
 * @param {number}  opts.currentFlightCount    - number of flights after filtering
 * @returns {{ boardRenderOk: boolean, boardVisibleRowCount: number, boardBlankReason: string }}
 */
export function useBoardHealthProbe({ containerRef, currentFlightCount }) {
  const [probeState, setProbeState] = useState(INITIAL_STATE);
  const latestRef = useRef(INITIAL_STATE);

  /** Commit result to ref + React state + singleton store */
  const commit = useCallback((result) => {
    latestRef.current = result;

    // Only trigger React re-render when something meaningful changed
    setProbeState(prev => {
      if (
        prev.boardRenderOk !== result.boardRenderOk ||
        prev.boardVisibleRowCount !== result.boardVisibleRowCount ||
        prev.boardBlankReason !== result.boardBlankReason
      ) {
        return result;
      }
      return prev;
    });

    updateProbeResults({
      renderOk:       result.boardRenderOk,
      visibleRowCount: result.boardVisibleRowCount,
      containerRect:  result.boardContainerRect,
      blankReason:    result.boardBlankReason,
    });
  }, []);

  /** Run probe against the live DOM */
  const runProbe = useCallback(() => {
    const result = {
      boardRenderOk: false,
      boardVisibleRowCount: 0,
      boardBlankReason: BoardBlankReason.UNKNOWN,
      boardContainerRect: { w: 0, h: 0 },
    };

    try {
      const container = containerRef?.current;

      // ── 1. Container exists? ────────────────────────────────────────
      if (!container) {
        result.boardBlankReason = BoardBlankReason.NO_CONTAINER;
        commit(result);
        return result;
      }

      // ── 2. Container has dimensions? ────────────────────────────────
      const rect = container.getBoundingClientRect();
      result.boardContainerRect = { w: Math.round(rect.width), h: Math.round(rect.height) };

      if (rect.width === 0 || rect.height === 0) {
        result.boardBlankReason = BoardBlankReason.ZERO_SIZE;
        commit(result);
        return result;
      }

      // ── 3. Container visible (CSS)? ─────────────────────────────────
      const style = window.getComputedStyle(container);
      if (style.display === 'none') {
        result.boardBlankReason = BoardBlankReason.CSS_HIDDEN;
        commit(result);
        return result;
      }
      if (style.visibility === 'hidden') {
        result.boardBlankReason = BoardBlankReason.CSS_HIDDEN;
        commit(result);
        return result;
      }
      if (parseFloat(style.opacity) < 0.01) {
        result.boardBlankReason = BoardBlankReason.CSS_HIDDEN;
        commit(result);
        return result;
      }

      // ── 4. Check for explicit empty-state element ───────────────────
      const emptyStateEl = container.querySelector('.board-empty-state');
      if (emptyStateEl) {
        const esRect = emptyStateEl.getBoundingClientRect();
        if (esRect.height > 0 && esRect.width > 0) {
          // Legitimate empty — board is "healthy" in the sense nothing is broken
          result.boardRenderOk = true;
          result.boardBlankReason = BoardBlankReason.EMPTY_SCHEDULE;
          commit(result);
          return result;
        }
      }

      // ── 4b. Check for BoardFallback in DOM (detects latched fallback) ─
      const fallbackEl = container.querySelector('.board-fallback');
      const isFallbackVisible = fallbackEl != null;

      // ── 5. Count visible data rows ──────────────────────────────────
      const dataRows = container.querySelectorAll('tr[class*="data-row"]');
      let visibleCount = 0;
      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowRect = row.getBoundingClientRect();
        if (rowRect.height > 0) {
          const rowStyle = window.getComputedStyle(row);
          if (rowStyle.display !== 'none' && rowStyle.visibility !== 'hidden') {
            visibleCount++;
          }
        }
      }
      result.boardVisibleRowCount = visibleCount;

      if (visibleCount > 0) {
        result.boardRenderOk = true;
        result.boardBlankReason = BoardBlankReason.HEALTHY;
        commit(result);
        return result;
      }

      // ── 6. Zero visible rows — classify reason ──────────────────────
      if (currentFlightCount === 0) {
        // Zero flights after filtering — legitimate empty
        result.boardRenderOk = true;
        result.boardBlankReason = BoardBlankReason.EMPTY_SCHEDULE;
        commit(result);
        return result;
      }

      // Data exists but zero rendered rows
      if (isFallbackVisible) {
        // BoardFallback is rendered inside the container while flights exist —
        // self-healing has shown fallback but it hasn't resolved yet
        result.boardBlankReason = BoardBlankReason.FALLBACK_LATCHED;
      } else {
        result.boardBlankReason = BoardBlankReason.ZERO_ROWS;
      }
      commit(result);
      return result;

    } catch (err) {
      console.error('[BoardHealthProbe] Probe error:', err.message);
      result.boardBlankReason = BoardBlankReason.UNKNOWN;
      commit(result);
      return result;
    }
  }, [containerRef, currentFlightCount, commit]);

  // ── MutationObserver ────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      // Debounce: run probe on next microtask to batch rapid mutations
      queueMicrotask(() => runProbe());
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => observer.disconnect();
  }, [containerRef, runProbe]);

  // ── Periodic poll ───────────────────────────────────────────────────────
  useEffect(() => {
    // Run immediately on mount
    const initialTimer = setTimeout(runProbe, 500);

    const intervalId = setInterval(runProbe, PROBE_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
    };
  }, [runProbe]);

  return probeState;
}

export default useBoardHealthProbe;
